import { useIOSPresentation } from '../../lib/web-presentation';
import { IOSOptionalDetails } from '../../ios-web/components';
import { useEffect, useState, type ReactNode } from 'react';
import { Button, EmptyState, Panel, StatusBadge } from '../../components';
import type { KnownFilingDeadline, TaxReturnResource } from '../../lib/api';
import './styles.css';

export type TaxObligationId = 'VAT' | 'PIT' | 'WHT' | 'CIT' | 'PAYE';
export type ObligationApplicability = 'applicable' | 'undetermined';
export type ReturnCapabilityState = 'available' | 'blocked' | 'not-implemented';

export interface ReturnCapability {
  state: ReturnCapabilityState;
  detail: string;
}

export interface ReturnCentreCapabilities {
  computation: ReturnCapability;
  preparation: ReturnCapability;
  filing: ReturnCapability;
  payment?: ReturnCapability;
}

export interface ReturnCentreObligation {
  id: TaxObligationId;
  label: string;
  description: string;
  cadence: string;
  periodLabel: string;
  authorityLabel: string;
  deadline?: KnownFilingDeadline;
  applicability: ObligationApplicability;
  taxReturn?: TaxReturnResource;
  interaction?: ReactNode;
  unavailableReason?: string;
  capabilities?: ReturnCentreCapabilities;
}

export interface ReturnCentreProps {
  subjectName: string;
  obligations: readonly ReturnCentreObligation[];
  initialObligation?: TaxObligationId;
  selectedObligation?: TaxObligationId;
  onSelectObligation?: (obligation: TaxObligationId) => void;
}

export function ReturnCentre({ subjectName, obligations, initialObligation, selectedObligation, onSelectObligation }: ReturnCentreProps) {
  const ios = useIOSPresentation();
  const defaultId = obligations.find((item) => item.id === initialObligation)?.id
    ?? obligations.find((item) => item.applicability === 'applicable'
      && item.capabilities?.computation.state === 'available')?.id
    ?? obligations[0]?.id;
  const [internalSelectedId, setInternalSelectedId] = useState(defaultId);
  const selectedId = selectedObligation ?? internalSelectedId;
  useEffect(() => {
    if (!obligations.some((item) => item.id === selectedId)) setInternalSelectedId(defaultId);
  }, [defaultId, obligations, selectedId]);
  const selected = obligations.find((item) => item.id === selectedId) ?? obligations[0];
  if (!selected) return <EmptyState title="No tax obligations available" description="No return obligations are configured for this tax subject." />;

  const recordedGuidance = returnStatusGuidance(selected.taxReturn?.status, selected.taxReturn?.track);
  const guidance = selected.taxReturn?.status === 'CALCULATED' && selected.capabilities?.preparation.state === 'blocked'
    ? { label: 'Calculated', nextAction: 'Review your saved figures and calculation details.',
      detail: 'Return preparation is not available for this route here yet. Your work remains saved.', tone: 'warning' as const }
    : recordedGuidance;
  return <section aria-label={`Return Centre for ${subjectName}`} className="am-return-centre">
    <Panel title="Tax returns" description="Choose a tax type to see its current status, what you can do now, and the next step.">
      {ios ? <label className="ios-context-field"><span>Tax type</span><select aria-label="Tax type" value={selected.id}
        onChange={(event) => { const id = event.target.value as TaxObligationId; setInternalSelectedId(id); onSelectObligation?.(id); }}>
        {obligations.map((obligation) => <option key={obligation.id} value={obligation.id}>{obligation.id} · {obligation.label} · {obligationStatusLabel(obligation)}</option>)}
      </select></label> : <div aria-label="Tax obligations" className="am-return-centre__obligations" role="list">
        {obligations.map((obligation) => <div key={obligation.id} role="listitem"><button aria-pressed={obligation.id === selected.id}
          className={`am-return-centre__obligation${obligation.id === selected.id ? ' is-selected' : ''}`}
          onClick={() => { setInternalSelectedId(obligation.id); onSelectObligation?.(obligation.id); }} type="button">
          <span><strong>{obligation.id}</strong><small>{obligation.label}</small></span>
          <StatusBadge tone={obligationTone(obligation)}>{obligationStatusLabel(obligation)}</StatusBadge>
        </button></div>)}
      </div>}
    </Panel>

    <Panel actions={<StatusBadge tone={selected.taxReturn ? guidance.tone : obligationTone(selected)}>
      {selected.taxReturn ? guidance.label : obligationStatusLabel(selected)}
    </StatusBadge>} className="am-return-centre__selected" title={`${selected.label} · ${selected.periodLabel}`}
      description={selected.description}>
      <IOSOptionalDetails title="Return details and available steps">
      <dl className="am-return-centre__meta">
        <div><dt>Tax subject</dt><dd>{subjectName}</dd></div>
        <div><dt>Frequency</dt><dd>{selected.cadence}</dd></div>
        <div><dt>Filing authority</dt><dd>{selected.authorityLabel}</dd></div>
        {selected.deadline && <div><dt>Known filing deadline</dt><dd>{formatDeadline(selected.deadline.dueDate)} · <StatusBadge
          tone={deadlineTone(selected.deadline)}>{selected.deadline.urgencyLabel}</StatusBadge></dd></div>}
        <div><dt>Recorded state</dt><dd>{guidance.label}</dd></div>
      </dl>
      {selected.deadline && <p className="am-return-centre__deadline-note"><a href={selected.deadline.rule.sourceUri}
        rel="noreferrer" target="_blank">{selected.deadline.rule.title}</a> · {selected.deadline.rule.locator}.{' '}
        {selected.deadline.rule.qualification}</p>}
      {selected.capabilities && <dl aria-label={`${selected.id} available steps`} className="am-return-centre__capabilities">
        {Object.entries(selected.capabilities).map(([name, capability]) => <div key={name}>
          <dt>{name}</dt><dd><StatusBadge tone={capability.state === 'available' ? 'success'
            : capability.state === 'blocked' ? 'warning' : 'neutral'}>
            {capability.state === 'available' ? 'available' : 'not available'}</StatusBadge>
            <small>{userCapabilityDetail(name, capability)}</small></dd>
        </div>)}
      </dl>}
      </IOSOptionalDetails>
      {selected.applicability === 'applicable' && selected.capabilities?.computation.state === 'available'
        ? <div className="am-return-centre__next" role="note">
        <strong>Next action</strong><p>{guidance.nextAction}</p>
        {guidance.detail && <small>{guidance.detail}</small>}
      </div> : <EmptyState compact title={unavailableTitle(selected)}
        description={selected.unavailableReason ?? defaultUnavailableReason(selected)} />}
    </Panel>

    {selected.applicability === 'applicable' && selected.capabilities?.computation.state === 'available'
      && selected.interaction}
  </section>;
}

function formatDeadline(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value
    : date.toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function deadlineTone(deadline: KnownFilingDeadline): 'neutral' | 'info' | 'warning' | 'danger' {
  if (deadline.urgency === 'DATE_PASSED' || deadline.urgency === 'DUE_TODAY' || deadline.urgency === 'URGENT') return 'danger';
  if (deadline.urgency === 'DUE_SOON' || deadline.urgency === 'COMING_UP') return 'warning';
  return 'info';
}

export function returnStatusGuidance(status?: string, track: TaxReturnResource['track'] = 'A'): { label: string; nextAction: string; detail?: string;
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' } {
  switch (status) {
    case undefined:
      return { label: 'Not started', nextAction: 'Enter or confirm your amounts, then calculate your tax position. You can add supporting documents as needed.',
        detail: 'No return exists for this obligation and period yet.', tone: 'neutral' };
    case 'DRAFT':
      return { label: 'Draft', nextAction: 'Confirm the saved figures for this taxpayer and period.',
        detail: 'A draft already exists. Continue it; do not create a duplicate.', tone: 'info' };
    case 'FACTS_ATTACHED':
      return { label: 'Records ready', nextAction: 'Calculate the tax position from the reviewed figures.',
        detail: 'Your figures are saved; the return still needs its calculation.', tone: 'info' };
    case 'CALCULATED':
      return { label: 'Calculated', nextAction: 'Review how the estimate was calculated, then prepare the return when that step becomes available.',
        detail: 'Amoye can explain the result but cannot approve or change it for you.', tone: 'warning' };
    case 'PREPARED':
      return track === 'A'
        ? { label: 'Prepared', nextAction: 'Assign a different authorised practitioner to review the prepared return.',
          detail: 'The preparer cannot complete their own professional review.', tone: 'warning' }
        : { label: 'Prepared', nextAction: 'Review this return and confirm your declaration before signing.', tone: 'warning' };
    case 'REVIEWED':
      return { label: 'Reviewed', nextAction: 'Have an authorised approver approve this exact reviewed version.', tone: 'warning' };
    case 'APPROVED':
      return { label: 'Approved', nextAction: 'Send this approved version and its declaration to the authorised client signatory.', tone: 'warning' };
    case 'SIGNED':
      return { label: 'Signed', nextAction: track === 'A' ? 'A separately authorised submitter can continue to filing when it is available.'
        : 'Continue to the available export or filing options for this signed version.',
        detail: 'Signing completes the declaration but does not mean the return has been filed.', tone: 'success' };
    case 'SUBMISSION_REQUESTED':
      return { label: 'Submission requested', nextAction: 'Amoye is checking the existing request. Wait for confirmation before sending it again.',
        detail: 'This state does not by itself prove NRS acceptance.', tone: 'info' };
    case 'DISPATCH_AMBIGUOUS':
      return { label: 'Filing status needs attention', nextAction: 'Check the existing filing request before trying again.',
        detail: 'The request may have been received. Trying again without checking could create a duplicate.', tone: 'danger' };
    case 'MOCK_ACCEPTED':
      return { label: 'Test received', nextAction: 'Review the test receipt. This is not confirmation from the tax authority.', tone: 'success' };
    case 'CORRECTION_REQUIRED':
      return { label: 'Correction required', nextAction: 'Start a correction or amendment so the submitted version remains in the history.', tone: 'danger' };
    default:
      return { label: status.replaceAll('_', ' ').toLowerCase(), nextAction: 'Open the return history and follow the recorded next authorised action.', tone: 'info' };
  }
}

function obligationStatusLabel(obligation: ReturnCentreObligation): string {
  if (obligation.applicability === 'undetermined') return 'Needs confirmation';
  if (obligation.taxReturn?.status) return obligation.taxReturn.status.replaceAll('_', ' ').toLowerCase();
  if (obligation.capabilities?.computation.state === 'available') return 'Available';
  if (obligation.capabilities && Object.values(obligation.capabilities)
    .every((capability) => capability.state === 'not-implemented')) return 'Not yet available';
  return 'Not yet available';
}

function unavailableTitle(obligation: ReturnCentreObligation): string {
  if (obligation.applicability === 'undetermined') return 'Confirm this obligation';
  if (obligation.capabilities && Object.values(obligation.capabilities)
    .every((capability) => capability.state === 'not-implemented')) return 'Not yet available';
  return 'Configured but not yet available';
}

function defaultUnavailableReason(obligation: ReturnCentreObligation): string {
  if (obligation.applicability === 'undetermined') {
    return 'Confirm this obligation from the tax subject’s legal form, registrations, activities and effective-period evidence.';
  }
  return 'This tax type is not available here yet. You can continue with the other tax work shown as available.';
}

function userCapabilityDetail(name: string, capability: ReturnCapability): string {
  if (capability.state === 'available') return capability.detail;
  if (name === 'computation') return 'Calculation is not available for this tax type yet.';
  if (name === 'preparation') return 'Complete the available calculation and review steps first.';
  if (name === 'filing') return 'You can continue preparing your records; filing will appear here when available.';
  if (name === 'payment') return 'Payment becomes available after there is an approved amount to pay.';
  return 'This step is not available yet.';
}

function obligationTone(obligation: ReturnCentreObligation): 'neutral' | 'info' | 'success' | 'warning' | 'danger' {
  if (obligation.applicability === 'undetermined') return 'warning';
  if (obligation.taxReturn?.status) return returnStatusGuidance(obligation.taxReturn.status).tone;
  return obligation.capabilities?.computation.state === 'available' ? 'success' : 'neutral';
}
